
import React, { useRef, useState, useEffect } from 'react';
import { GeneratedImage, Annotation, ToolMode, SelectionRect } from '../types';
import { ZoomIn, ZoomOut, Download, Move, Eraser, MousePointer2, Check, X, Sparkles, Video } from 'lucide-react';

interface ImageCanvasProps {
  image: GeneratedImage | null;
  seriesImages: GeneratedImage[];
  onSelectSeriesImage: (img: GeneratedImage) => void;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  isGenerating: boolean;
  toolMode: ToolMode;
  setToolMode: (mode: ToolMode) => void;
  onInPaint: (selection: SelectionRect, prompt: string) => Promise<void>;
  focusedAnnotationId: string | null;
}

const ImageCanvas: React.FC<ImageCanvasProps> = ({ 
  image, 
  seriesImages, 
  onSelectSeriesImage,
  annotations, 
  setAnnotations, 
  isGenerating,
  toolMode,
  setToolMode,
  onInPaint,
  focusedAnnotationId
}) => {
  const [zoom, setZoom] = useState(1);
  const imgRef = useRef<HTMLDivElement>(null);
  
  // Selection State for In-Painting
  const [isSelecting, setIsSelecting] = useState(false);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [startPos, setStartPos] = useState<{x: number, y: number} | null>(null);
  const [inPaintPrompt, setInPaintPrompt] = useState('');
  const [showInPaintModal, setShowInPaintModal] = useState(false);
  const [isRefining, setIsRefining] = useState(false);

  // Dragging State for Annotations
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Mouse Handlers for Drawing Selection or Labels
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!image || image.mediaType === 'VIDEO') return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (toolMode === ToolMode.INPAINT) {
        setStartPos({ x, y });
        setSelection({ x, y, width: 0, height: 0 });
        setIsSelecting(true);
    }
  };

  const handleLabelMouseDown = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
    if (toolMode === ToolMode.POINTER) {
      e.stopPropagation(); // Prevent creating new label or other canvas actions
      setDraggingId(id);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((!isSelecting && !draggingId) || !image) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = ((e.clientX - rect.left) / rect.width) * 100;
    const currentY = ((e.clientY - rect.top) / rect.height) * 100;

    // Handle InPaint Selection
    if (isSelecting && startPos) {
        const width = Math.abs(currentX - startPos.x);
        const height = Math.abs(currentY - startPos.y);
        const x = Math.min(currentX, startPos.x);
        const y = Math.min(currentY, startPos.y);
        setSelection({ x, y, width, height });
    }

    // Handle Annotation Dragging
    if (draggingId) {
       setAnnotations(prev => prev.map(ann => {
         if (ann.id === draggingId) {
           // Clamp to canvas bounds
           return {
             ...ann,
             labelX: Math.max(0, Math.min(100, currentX)),
             labelY: Math.max(0, Math.min(100, currentY))
           };
         }
         return ann;
       }));
    }
  };

  const handleMouseUp = () => {
    // Handle InPaint selection completion
    if (isSelecting && selection && selection.width > 2 && selection.height > 2) {
      setShowInPaintModal(true);
    } else if (isSelecting) {
      setSelection(null); // Clear tiny selections
    }

    setIsSelecting(false);
    setStartPos(null);
    setDraggingId(null);
  };

  // Click Handler for Labels (Pointer Mode) - Creating New Label
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!image || image.mediaType === 'VIDEO' || toolMode !== ToolMode.POINTER || isSelecting || draggingId) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newAnnotation: Annotation = {
      id: Date.now().toString(),
      targetX: x,
      targetY: y,
      labelX: Math.min(x + 15, 90), // Offset label by default
      labelY: Math.max(y - 15, 5),
      text: "New Label"
    };
    
    setAnnotations(prev => [...prev, newAnnotation]);
  };

  const handleRefineSubmit = async () => {
    if (!selection || !inPaintPrompt) return;
    setIsRefining(true);
    await onInPaint(selection, inPaintPrompt);
    setIsRefining(false);
    setShowInPaintModal(false);
    setSelection(null);
    setInPaintPrompt('');
    setToolMode(ToolMode.POINTER); // Reset tool
  };

  const downloadImage = () => {
    if (!image) return;
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `rj-medgraph-${Date.now()}.${image.mediaType === 'VIDEO' ? 'mp4' : 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex-1 bg-slate-950 relative flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="h-14 border-b border-slate-800 bg-slate-900 flex items-center justify-between px-4 shadow-sm z-10">
        <div className="flex items-center gap-4">
          <div className="flex bg-slate-800 rounded p-1 border border-slate-700">
             <button 
               onClick={() => setToolMode(ToolMode.POINTER)}
               disabled={image?.mediaType === 'VIDEO'}
               className={`px-3 py-1.5 rounded flex gap-2 items-center text-xs font-medium transition-all ${toolMode === ToolMode.POINTER ? 'bg-slate-700 text-cyan-400 shadow-sm ring-1 ring-cyan-500/20' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'} ${image?.mediaType === 'VIDEO' ? 'opacity-50 cursor-not-allowed' : ''}`}
               title="Pointer Tool: Add and edit labels"
             >
               <MousePointer2 className="w-4 h-4" /> 
               <span>Pointer</span>
             </button>
             <div className="w-px bg-slate-700 mx-1 my-1"></div>
             <button 
               onClick={() => setToolMode(ToolMode.INPAINT)}
               disabled={image?.mediaType === 'VIDEO'}
               className={`px-3 py-1.5 rounded flex gap-2 items-center text-xs font-medium transition-all ${toolMode === ToolMode.INPAINT ? 'bg-slate-700 text-amber-400 shadow-sm ring-1 ring-amber-500/20' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'} ${image?.mediaType === 'VIDEO' ? 'opacity-50 cursor-not-allowed' : ''}`}
               title="Inpaint Tool: Select regions to refine"
             >
               <Eraser className="w-4 h-4" /> 
               <span>Inpaint</span>
             </button>
          </div>
          {toolMode === ToolMode.INPAINT && image?.mediaType !== 'VIDEO' && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-4">
              <div className="relative group">
                 <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                   <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                 </div>
                 <input 
                   type="text" 
                   value={inPaintPrompt}
                   onChange={(e) => setInPaintPrompt(e.target.value)}
                   placeholder="Describe fix (e.g. 'remove cyst')"
                   className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 w-48 sm:w-64 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder:text-slate-500"
                 />
              </div>
              <span className="text-[10px] text-slate-500 font-medium hidden sm:inline-block">
                 &rarr; Select area on image
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white"><ZoomOut className="w-5 h-5" /></button>
          <span className="text-xs text-slate-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.1))} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white"><ZoomIn className="w-5 h-5" /></button>
          <div className="w-px h-6 bg-slate-700 mx-2"></div>
          <button onClick={downloadImage} disabled={!image} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-cyan-400 disabled:opacity-50"><Download className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Viewport */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-8 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:20px_20px] relative">
        {isGenerating ? (
          <div className="text-center z-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500 mb-4"></div>
            <p className="text-slate-400 text-sm animate-pulse">Rendering anatomical data...</p>
          </div>
        ) : image ? (
          <div 
            ref={imgRef}
            className={`relative shadow-2xl transition-transform duration-200 ease-out border-4 border-white group select-none
              ${toolMode === ToolMode.POINTER && image.mediaType !== 'VIDEO' && !draggingId ? 'cursor-crosshair' : ''}
              ${toolMode === ToolMode.INPAINT && image.mediaType !== 'VIDEO' ? 'cursor-cell' : ''}
              ${draggingId ? 'cursor-grabbing' : ''}
            `}
            style={{ transform: `scale(${zoom})`, touchAction: 'none' }}
            onClick={handleImageClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {image.mediaType === 'VIDEO' ? (
               <video 
                 src={image.url} 
                 controls 
                 loop 
                 autoPlay 
                 muted 
                 className="max-w-[800px] bg-black block"
               />
            ) : (
               <img src={image.url} alt="Medical Generation" className="max-w-[800px] bg-white block pointer-events-none" />
            )}
            
            {/* SVG Overlay for Connectors (Images Only) */}
            {image.mediaType !== 'VIDEO' && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                   <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                      <polygon points="0 0, 6 2, 0 4" fill="#06b6d4" />
                   </marker>
                   <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                     <feDropShadow dx="1" dy="1" stdDeviation="1" floodColor="#000000" floodOpacity="0.5"/>
                   </filter>
                </defs>
                {annotations.map(ann => {
                  const isFocused = ann.id === focusedAnnotationId;
                  const isDragging = ann.id === draggingId;
                  return (
                    <line 
                      key={`line-${ann.id}`}
                      x1={`${ann.labelX}%`} y1={`${ann.labelY}%`} 
                      x2={`${ann.targetX}%`} y2={`${ann.targetY}%`} 
                      stroke={isFocused || isDragging ? "#22d3ee" : "#06b6d4"}
                      strokeWidth={isFocused || isDragging ? "3" : "2"}
                      markerEnd="url(#arrowhead)"
                      filter="url(#shadow)"
                      className="transition-all duration-75"
                    />
                  );
                })}
              </svg>
            )}

            {/* In-Painting Selection Box */}
            {selection && image.mediaType !== 'VIDEO' && (
              <div 
                className="absolute border-2 border-dashed border-amber-400 bg-amber-400/10 pointer-events-none z-20"
                style={{
                  left: `${selection.x}%`,
                  top: `${selection.y}%`,
                  width: `${selection.width}%`,
                  height: `${selection.height}%`
                }}
              />
            )}

            {/* Annotations Layer (Images Only) */}
            {image.mediaType !== 'VIDEO' && annotations.map((ann) => {
              const isFocused = ann.id === focusedAnnotationId;
              const isDragging = ann.id === draggingId;
              return (
                <div 
                  key={ann.id}
                  className={`absolute z-10 transition-transform duration-75 
                    ${isFocused ? 'scale-110 z-50' : ''} 
                    ${isDragging ? 'scale-110 z-50' : 'z-10'}
                  `}
                  style={{ left: `${ann.labelX}%`, top: `${ann.labelY}%`, transform: 'translate(-50%, -50%)' }}
                  onMouseDown={(e) => handleLabelMouseDown(e, ann.id)}
                  onClick={(e) => e.stopPropagation()} // Stop propagation to prevent "Add Label" on canvas
                >
                  <div className={`bg-white/90 backdrop-blur text-slate-900 text-xs px-2 py-1 rounded shadow-lg font-bold border border-slate-300 whitespace-nowrap 
                    ${isFocused ? 'border-cyan-500 ring-2 ring-cyan-400/50' : ''}
                    ${toolMode === ToolMode.POINTER ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
                  `}>
                     {ann.text}
                  </div>
                </div>
              );
            })}
            
            {/* Refine Spinner Overlay */}
            {isRefining && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30 backdrop-blur-sm">
                 <div className="flex flex-col items-center gap-2">
                    <Sparkles className="w-8 h-8 text-amber-400 animate-spin" />
                    <span className="text-white font-medium text-sm text-shadow">Refining Anatomy...</span>
                 </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center max-w-md p-8 border-2 border-dashed border-slate-800 rounded-xl">
             <Move className="w-12 h-12 text-slate-700 mx-auto mb-4" />
             <h3 className="text-slate-300 font-medium mb-2">Ready to Generate</h3>
             <p className="text-slate-500 text-sm">Configure your parameters in the Builder to generate high-fidelity medical illustrations.</p>
          </div>
        )}
        
        {/* In-Paint Modal Popover */}
        {showInPaintModal && selection && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-slate-800 p-4 rounded-lg shadow-2xl border border-slate-700 z-50 w-80 animate-in fade-in zoom-in duration-200">
             <h4 className="text-white font-bold mb-2 flex items-center gap-2 text-sm">
               <Sparkles className="w-4 h-4 text-amber-400" /> Refine Selection
             </h4>
             <textarea 
               autoFocus
               className="w-full bg-slate-900 text-white text-sm p-2 rounded border border-slate-600 focus:border-amber-400 outline-none mb-3"
               rows={3}
               placeholder="e.g. Fix the shape of the kidney, remove the artifact..."
               value={inPaintPrompt}
               onChange={(e) => setInPaintPrompt(e.target.value)}
             />
             <div className="flex gap-2 justify-end">
               <button 
                 onClick={() => { setSelection(null); setShowInPaintModal(false); }}
                 className="px-3 py-1.5 rounded text-xs font-medium text-slate-300 hover:bg-slate-700"
               >
                 Cancel
               </button>
               <button 
                 onClick={handleRefineSubmit}
                 className="px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-500 flex items-center gap-1"
               >
                 Apply Fix
               </button>
             </div>
          </div>
        )}
      </div>

      {/* Series Gallery Strip */}
      {seriesImages.length > 0 && (
         <div className="h-24 bg-slate-900 border-t border-slate-800 p-2 flex items-center gap-2 overflow-x-auto z-10">
            {seriesImages.map((img) => (
              <button 
                key={img.id}
                onClick={() => onSelectSeriesImage(img)}
                className={`relative h-full aspect-square rounded overflow-hidden border-2 transition-all flex-shrink-0 group
                  ${image?.id === img.id ? 'border-cyan-500 scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <img src={img.url} className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 w-full bg-black/60 text-[10px] text-white p-1 truncate text-center backdrop-blur-sm">
                  {img.label || 'View'}
                </div>
              </button>
            ))}
         </div>
      )}

      {/* Disclaimer Banner */}
      <div className="bg-amber-900/20 border-t border-amber-900/50 p-2 text-center">
         <p className="text-[10px] text-amber-500/80 font-medium tracking-wide uppercase">
          AI Generated content for educational purposes only. Not for clinical diagnosis.
         </p>
      </div>
    </div>
  );
};

export default ImageCanvas;
